import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { PolotnoContainer, SidePanelWrap, WorkspaceWrap } from 'polotno';
import { runInAction } from 'mobx';
import { EditableText, Tooltip } from '@blueprintjs/core';
import { Toolbar } from 'polotno/toolbar/toolbar';
import { Workspace } from 'polotno/canvas/workspace';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import ZoomButtons from 'polotno/toolbar/zoom-buttons';
import FontComponant from '../../../design/FontComponant';
import TimeLineControllers from './TimeLineControllers';
import CustomPageTimeline from './CustomPageTimeline';
import { dimensions } from '../../../utils/helper';
import SmallThumbnails from './SmallThumbnails';
import { changeDpiDataUrl } from 'changedpi';
import { useDispatch, useSelector } from 'react-redux';
import { setDesignStore } from '../../../redux/slices/variationSlice';
import InformationPopup from '../../../popup/InformationPopup';
import { designScreenThumbs } from '../../../redux/slices/ThumbnailSlice';
import DesignSidebar from '../../../components/DesignSidebar';
import VariationCountLabel from '../../../components/VariationCountLabel';

//  export default function PolotnoDesignNew({ store }) {

const PolotnoDesignNew = observer(({ store }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userPlanDetails = useSelector((state) => state.userPlan);
  const sizeDimensions = useSelector((state) => state.sizeDimension.dimensions);
  const selectedType = useSelector((state) => state.sizeDimension.selectedType);
  const { designThumbs } = useSelector((state) => state.thumbnails);

  const [sizeFormat, setSizeFormat] = useState('inch');
  const [showTooltip, setShowTooltip] = useState(false);
  const [showTimeLine, setShowTimeline] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [images, setImages] = useState([]);
  const [showSmallThumbs, setShowSmallThumbs] = useState(true);
  const [isBgEditable, setIsBgEditable] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [showInformationPopup, setInformationPopup] = useState(false);
  const [generatingThumb, setGeneratingThumb] = useState(false);
  const [selectedThumbPage, setSelectedThumbPage] = useState('');
  const [preservedImages, setPreservedImages] = useState([]);
  const [activeTab, setActiveTab] = useState('design');
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  // console.log("deepak polotno store activev page is", store.activePage.id);

  // Initialize images from Redux store or local storage
  useEffect(() => {
    const savedImages = localStorage.getItem('polotnoDesignImages');
    if (savedImages) {
      const parsedImages = JSON.parse(savedImages);
      setImages(parsedImages);
      dispatch(designScreenThumbs(parsedImages));
    } else if (designThumbs && designThumbs.length > 0) {
      setImages(designThumbs);
    }
  }, []);

  // Save images to local storage whenever they change
  useEffect(() => {
    if (images.length > 0) {
      localStorage.setItem('polotnoDesignImages', JSON.stringify(images));
    }
  }, [images]);

  const handleUnlockClick = () => {
    const activePage = store.activePage;
    if (!activePage) return;

    setIsBgEditable((prev) => {
      runInAction(() => {
        const existingBg = activePage.children.find(
          (child) => child.name === 'background-image'
        );

        if (existingBg) {
          if (!prev) {
            // Unlock background (Make draggable & selectable)
            existingBg.set({
              selectable: true,
              evented: true,
              lockScalingX: false,
              lockScalingY: false,
              lockMovementX: false,
              lockMovementY: false,
            });
          } else {
            // Lock background (Prevent dragging & scaling)
            existingBg.set({
              selectable: false,
              evented: false,
              lockScalingX: true,
              lockScalingY: true,
              lockMovementX: true,
              lockMovementY: true,
            });
          }
        } else {
          // Convert background to image only if it doesn't exist
          const bgValue = activePage.background;
          if (!bgValue || bgValue === 'transparent') return;

          runInAction(() => {
            activePage.set({ background: 'transparent' });

            activePage.addElement({
              id: `background-image-${activePage.id}`,
              name: 'background-image',
              type: 'image',
              src: bgValue,
              x: 0,
              y: 0,
              width: activePage.width,
              height: activePage.height,
              selectable: true,
              evented: true,
              lockScalingX: false,
              lockScalingY: false,
              lockMovementX: false,
              lockMovementY: false,
            });
          });
        }
      });

      return !prev;
    });
  };

  const convertBackgroundToImage = (masterPage, targetPage) => {
    if (!masterPage || !targetPage) return;

    const bgValue = masterPage.background; // Background color or image URL

    // Initially, set the background (locked)
    targetPage.set({ background: bgValue });
  };

  const syncChanges = async (masterPage, targetPage) => {
    runInAction(() => {
      console.log('Syncing changes from master page to target page');

      // 1. Convert master page background to image on target page
      convertBackgroundToImage(masterPage, targetPage);

      // 2. Create a mapping of target page elements by ID
      const targetElementsMap = new Map(
        targetPage.children.map((child) => [child.id, child])
      );

      // 3. Get removed elements from both master and target pages
      const removedElements = new Set([
        ...(targetPage.custom?.removedElements || []),
        ...(masterPage.custom?.removedElements || []),
      ]);

      // 4. Iterate over master page elements
      masterPage.children.forEach((masterObject) => {
        const masterData = masterObject.toJSON();

        // Skip removed elements
        if (removedElements.has(masterData.id)) return;

        // 5. Generate a unique ID for each master element to sync with the target
        const uniqueId = `${masterData.id}-${targetPage.id}`;

        // 6. Find corresponding target element
        const targetObject = targetElementsMap.get(uniqueId);

        // 7. Calculate relative position based on the master page's size
        const relativeX = masterData.x / masterPage.width;
        const relativeY = masterData.y / masterPage.height;

        // 8. Adjust X position based on the relative position logic
        let newX;
        if (relativeX >= 0.05 && relativeX <= 0.55) {
          // Center the element horizontally on the target page if it's near the center of the master page
          newX = (targetPage.width - masterData.width) / 2;
        } else {
          newX = targetPage.width * relativeX;
        }

        // 9. Calculate new Y position based on target page height
        const newY = targetPage.height * relativeY;

        // 10. Update or create target element
        if (targetObject) {
          // Update existing target element with master data
          const updatedObject = {
            ...masterData,
            align: 'center', // Example, you may adjust alignment based on the actual logic
            id: targetObject.id, // Use targetObject's ID
            x: newX,
            y: newY,
          };
          targetObject.set(updatedObject);
        } else {
          // Add new element to target page if it doesn't exist
          const newObject = {
            align: 'center', // Example, adjust based on your requirement
            ...masterData,
            id: uniqueId,
            x: newX,
            y: newY,
          };
          targetPage.addElement(newObject);
        }
      });
    });

    // After syncing, save the updated state as JSON
    // setStoreJson(JSON.stringify(store.toJSON()));
  };

  const resetHandler = () => {
    try {
      setGeneratingThumb(true);
      if (sizeDimensions && sizeDimensions?.M) {
        store.clear();
        const dpi = 72;
        const masterPage = store.addPage({
          name: 'master',
          width: sizeDimensions.M.inchWidth * dpi,
          height: sizeDimensions.M.inchHeight * dpi,
          id: 'master',
          background: 'rgba(0,0,0,0)',
          custom: {
            size: 'M',
            inch: sizeDimensions['M']?.inch,
            pixels: `${sizeDimensions['M']?.width} X ${sizeDimensions['M']?.height} px`,
          },
        });
        const addAllSizePages = async () => {
          Object.keys(sizeDimensions)?.forEach((size) => {
            const item = sizeDimensions[size];
            store.addPage({
              name: size.split(' ').join('_'),
              width: item.inchWidth * 72,
              height: item.inchHeight * 72,
              id: size.split(' ').join('_'),
              background: 'rgba(0,0,0,0)',
              custom: {
                size: size,
                inch: sizeDimensions[size]?.inch,
                pixels: `${sizeDimensions[size]?.width} X ${sizeDimensions[size]?.height}`,
              },
            });
          });
        };
        addAllSizePages();
      }
      processAllPages();
    } catch (error) {
      console.log('reset canvas error ', error);
    } finally {
      setGeneratingThumb(false);
    }
  };

  const refreshHandler = async () => {
    setGeneratingThumb(true);
    setIsChanged(true);
    const masterPage = store.pages.find((page) => page.id === 'master');
    store.pages.forEach(async (page) => {
      if (page.id !== 'master') {
        const arr = [page];
        await Promise.all(
          arr.map(async (item) => {
            try {
              await syncChanges(masterPage, item);
            } catch (error) {
              console.log('error is', error);
            }
          })
        );
        await sleep(50);
      }
    });
    let imgArr = [];
    for (let i = 0; i < store.pages.length; i++) {
      const blob = await store.toDataURL({
        pageId: store.pages[i].id,
        quality: 0.1,
        mimeType: 'image/jpeg',
      });
      imgArr.push({
        id:
          i === 0
            ? 'master'
            : sizeDimensions
            ? Object.keys(sizeDimensions)[i - 1]?.split(' ').join('_')
            : 'nosize',
        image: changeDpiDataUrl(blob, 72),
        label:
          i === 0
            ? 'Master'
            : sizeDimensions
            ? Object.keys(sizeDimensions)[i - 1]
            : 'nosize',
      });
    }
    console.log('imgArr', imgArr);
    dispatch(designScreenThumbs(imgArr));
    setImages(imgArr);
    setGeneratingThumb(false);
  };
  const sizeChangeHandler = (e) => {
    const val = e.target.value;
    setSizeFormat(val);
    store.setUnit({
      unit: val === 'inch' ? 'in' : 'px',
      dpi: 72,
    });
  };

  const handlePageClick = (id) => {
    setShowTimeline((prev) => !prev);
    setShowSmallThumbs(true);
    setSelectedThumbPage(id);
  };
  const TextAiWrite = () => null;
  const customFontFamily = () => <FontComponant store={store} />;

  const smallThumbHandler = () => setShowSmallThumbs((prev) => !prev);
  const timlineHandler = () => {
    setShowTimeline((prev) => !prev);
    setShowSmallThumbs(false);
  };

  const generateImage = async (index) => {
    try {
      if (!store || !store.pages || !store.pages[index]) {
        console.log("Store or page not initialized");
        return null;
      }

      const page = store.pages[index];
      console.log("Generating image for page:", page.id);

      // Add a small delay to ensure the workspace is rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      // Generate higher quality thumbnail
      const image = await store.toDataURL({
        mimeType: 'image/jpeg',
        pageId: page.id,
        quality: 0.8, // Increased quality
        pixelRatio: 1.5 // Higher resolution
      });

      if (!image) {
        console.log("Failed to generate image for page:", page.id);
        return null;
      }

      return changeDpiDataUrl(image, 72);
    } catch (error) {
      console.error('Error in generating image:', error);
      return null;
    }
  };

  const processAllPages = () => {
    setIsProcessing(true);
    const totalPages = store?.pages?.length;
    const imagesGenerated = [];

    const processPage = async (pageIndex) => {
      if (pageIndex < totalPages) {
        try {
          const image = await generateImage(pageIndex);
          
          if (image) {
            imagesGenerated.push({
              image,
              label:
                pageIndex === 0
                  ? 'Master'
                  : sizeDimensions
                  ? Object.keys(sizeDimensions)[pageIndex - 1]
                  : 'nosize',
              id:
                pageIndex === 0
                  ? 'master'
                  : sizeDimensions
                  ? Object.keys(sizeDimensions)[pageIndex - 1]?.split(' ').join('_')
                  : 'nosize',
            });

            // Update images state incrementally
            setImages([...imagesGenerated]);
            dispatch(designScreenThumbs([...imagesGenerated]));
          }

          requestAnimationFrame(() => {
            processPage(pageIndex + 1);
          });
        } catch (error) {
          console.error('Error processing page:', error);
          setIsProcessing(false);
        }
      } else {
        if (imagesGenerated.length > 0) {
          setImages(imagesGenerated);
          dispatch(designScreenThumbs(imagesGenerated));
          // Save to local storage
          localStorage.setItem('polotnoDesignImages', JSON.stringify(imagesGenerated));
        }
        setIsProcessing(false);
      }
    };

    // Start processing after a short delay to ensure store is ready
    setTimeout(() => {
      processPage(0);
    }, 500);
  };

  const handleVariationClick = () => {
    try {
      // Save current state before navigating
      localStorage.setItem('polotnoDesignStore', JSON.stringify(store.toJSON()));
      dispatch(setDesignStore(JSON.stringify(store.toJSON())));
      navigate('/variation', {
        state: {
          isChanged: isChanged,
        },
      });
    } catch (error) {
      console.log('variation redirection error', error);
    }
  };

  const handleExportClick = () => {
    try {
      const variation = localStorage.getItem('tshirtDetails');
      if (variation) {
        dispatch(setDesignStore(JSON.stringify(store.toJSON())));
        navigate('/export', {
          state: {
            storeVal: JSON.stringify(store.toJSON()),
            isChanged: isChanged,
            from: 'design',
          },
        });
      } else {
        setInformationPopup(true);
      }
    } catch (error) {
      console.log('error in export click');
    }
  };

  useEffect(() => {
    processAllPages();
  }, []);
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Alt') {
        e.preventDefault();
        setShowTooltip(e.type === 'keydown');
      }
    };

    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('keyup', handleKey, true);

    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('keyup', handleKey, true);
    };
  }, []);

  useEffect(() => {
    if (selectedThumbPage) {
      setTimeout(() => {
        store.selectPage(selectedThumbPage);
      }, 500);
    }
  }, [selectedThumbPage]);

  useEffect(() => {
    if (selectedType) {
      if (selectedType !== 'a4') {
        if (sizeDimensions && sizeDimensions?.M) {
          store.clear();
          const dpi = 72;
          const masterPage = store.addPage({
            name: 'master',
            width: sizeDimensions.M.inchWidth * dpi,
            height: sizeDimensions.M.inchHeight * dpi,
            id: 'master',
            background: 'rgba(0,0,0,0)',
            custom: {
              size: 'M',
              inch: sizeDimensions['M']?.inch,
              pixels: `${sizeDimensions['M']?.width} X ${sizeDimensions['M']?.height} px`,
            },
          });
          const addAllSizePages = async () => {
            Object.keys(sizeDimensions)?.forEach((size) => {
              const item = sizeDimensions[size];
              store.addPage({
                name: size.split(' ').join('_'),
                width: item.inchWidth * 72,
                height: item.inchHeight * 72,
                id: size.split(' ').join('_'),
                background: 'rgba(0,0,0,0)',
                custom: {
                  size: size,
                  inch: sizeDimensions[size]?.inch,
                  pixels: `${sizeDimensions[size]?.width} X ${sizeDimensions[size]?.height}`,
                },
              });
            });
          };
          addAllSizePages();
        }
      } else {
        const dpi = 72;
        const masterPage = store.addPage({
          name: 'master',
          width: sizeDimensions.inchWidth * dpi,
          height: sizeDimensions.inchHeight * dpi,
          id: 'master',
          background: 'rgba(0,0,0,0)',
          custom: {
            size: 'M',
            inch: sizeDimensions?.inch,
            pixels: `${sizeDimensions?.width} X ${sizeDimensions?.height} px`,
          },
        });
      }
    } else {
      alert('Please select the artboard first!');
      // navigate('/Home');
      window.location.href = "/";
    }
  }, [sizeDimensions, selectedType]);

  // Add useEffect to restore images when returning to design tab
  useEffect(() => {
    if (activeTab === 'design') {
      // Select the master page
      store.selectPage('master');
  
      // Optional: Scroll to top of the workspace
      setTimeout(() => {
        const workspaceContainer = document.querySelector('.polotno-workspace');
        if (workspaceContainer) {
          workspaceContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 200);
  
      // Restore thumbnails
      if (preservedImages.length > 0) {
        setImages(preservedImages);
        dispatch(designScreenThumbs(preservedImages));
      }
    }
  }, [activeTab]);
  
  // Restore store state when component mounts
  useEffect(() => {
    const savedStore = localStorage.getItem('polotnoDesignStore');
    if (savedStore) {
      try {
        const storeData = JSON.parse(savedStore);
        store.loadJSON(storeData);
      } catch (error) {
        console.error('Error restoring store state:', error);
      }
    }
  }, []);

  return (
    <>
      {showInformationPopup && (
        <InformationPopup
          closePopup={() => setInformationPopup(false)}
          message={'Add Variation first!'}
        />
      )}
      <PolotnoContainer
        style={{ width: '100vw', height: '100vh' }}
        className='polotno-container'>
        <SidePanelWrap className='polotno-sidebar'>
          <Link to='/' className='sidebar-logo' onClick={(e) => {
            e.preventDefault(); 
            window.location.href = "/";
          }}>
            <img
              src={'/assets/images/teedex-logo.png'}
              alt='Varient logo'
              className='sidebar-logo-main'
            />
            <img
              src={'/assets/images/teedex-logo-hover.png'}
              alt='Varient logo'
              className='sidebar-logo-hover'
            />
          </Link>
          <Sidebar store={store} />
          <div className='left-main-menu designSidebar'>
            <DesignSidebar />
          </div>
        </SidePanelWrap>
        <WorkspaceWrap className='polotno-workspace'>
          {!showTimeLine && (
            <>
              <div
                className='artboard--main__header--center'
                style={{ position: 'relative' }}>
                <div className='upgradeButtonFixedHome'>
                  <VariationCountLabel userPlanDetails={userPlanDetails} />
                </div>
                <div className='artboard--main__tabs'>
                  <button
                    className='artboard--main__tabs--nav__item active'
                    // onClick={navigateHomePage}
                  >
                    Design
                  </button>
                  <button
                    className='artboard--main__tabs--nav__item'
                    onClick={handleVariationClick}>
                    Variation
                  </button>
                  <button
                    className='artboard--main__tabs--nav__item'
                    onClick={handleExportClick}>
                    Export
                  </button>
                </div>
              </div>
              <div className='artboard--main__header--right variation-button'>
                <a onClick={handleVariationClick}>
                  <img
                    src={'/assets/images/list_white-icn.svg'}
                    alt='Variation Icon'
                  />
                  Add Variation
                </a>
              </div>
            </>
          )}
          <Toolbar
            store={store}
            components={{ TextFontFamily: customFontFamily, TextAiWrite }}
          />
          <Workspace
            // renderOnlyActivePage={renderOnlyActivePage}
            store={store}
            components={{
              PageControls: ({ store, page, xPadding, yPadding }) => {
                const hasManyPages = store.pages.length > 1;
                const index = store.pages.indexOf(page);
                return (
                  <div className='artboard-page-controls'>
                    <div
                      style={{
                        position: 'absolute',
                        top: yPadding - 20 + 'px',
                        left: xPadding + 'px',
                      }}
                      className='artboard-page'>
                      <EditableText
                        disabled
                        className='artboard-page-name'
                        value={
                          page.id?.split('_').join(' ')?.toUpperCase() ||
                          'Untitled page'
                        }
                        onChange={(val) => {
                          page.set({
                            custom: {
                              ...page.custom,
                              name: val,
                            },
                          });
                        }}
                      />
                      <EditableText
                        className='artboard-page-size'
                        disabled
                        value={page.custom[sizeFormat]}
                        onChange={(val) => {
                          page.set({
                            custom: {
                              ...page.custom,
                              name: val,
                            },
                          });
                        }}
                      />
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        top: yPadding - 30 + 'px',
                        right: xPadding + 'px',
                      }}
                      className='artboard-sizes'>
                      <select value={sizeFormat} onChange={sizeChangeHandler}>
                        <option value={'pixels'}>Pixels</option>
                        <option value={'inch'}>Inch</option>
                      </select>
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        top: yPadding - 40 + 'px',
                        right: xPadding + 'px',
                      }}></div>
                  </div>
                );
              },
              TextAiWrite,
              Tooltip: showTooltip ? Tooltip : null,
            }}
          />
          <div className='zoombar'>
            <ZoomButtons store={store} />
          </div>
          {showSmallThumbs && selectedType && selectedType !== 'a4' && (
            <SmallThumbnails
              generatingThumb={generatingThumb}
              currentActivevPage={store?.activePage?.id}
              store={store}
              images={images}
            />
          )}
          <TimeLineControllers
            timlineHandler={timlineHandler}
            smallThumbHandler={smallThumbHandler}
            showTimeline={showTimeLine}
            showSmallThumbs={showSmallThumbs}
            refreshHandler={refreshHandler}
            handleUnlockClick={handleUnlockClick}
            resetHandler={resetHandler}
            selectedType={selectedType}
          />
          {showTimeLine && selectedType && selectedType !== 'a4' && (
            <CustomPageTimeline
              store={store}
              // allThumbs={allThumbs}
              allThumbs={images}
              handlePageClick={handlePageClick}
              currentActivevPage={store.activePage.id}
            />
          )}

          {/* </div> */}
        </WorkspaceWrap>
      </PolotnoContainer>
    </>
  );
});

export default PolotnoDesignNew;